import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useStoreSettings } from '@/hooks/useStoreSettings';

interface Subscriber {
  id: number;
  phone: string;
  whatsapp: string;
  status: 'pending' | 'notified' | 'cancelled';
}

interface RestockNotifyModalProps {
  productId: number;
  productName: string;
  newStock: number;
  onClose: () => void;
  onDone: (notifiedCount: number) => void;
}

export default function RestockNotifyModal({
  productId,
  productName,
  newStock,
  onClose,
  onDone,
}: RestockNotifyModalProps) {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [done, setDone] = useState(false);
  const { settings } = useStoreSettings();

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('stock_notifications')
      .select('id, phone, whatsapp, status')
      .eq('product_id', productId)
      .eq('status', 'pending');
    if (!error && data) setSubscribers(data as Subscriber[]);
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleSendAll = async () => {
    if (subscribers.length === 0) return;
    setSending(true);
    let count = 0;

    for (const sub of subscribers) {
      const phone = (sub.whatsapp || sub.phone).replace(/^0/, '966');
      const msg = encodeURIComponent(
        `🔔 *مرحباً!*\n\nالمنتج الذي طلبت الإشعار عنه أصبح متوفراً الآن:\n\n*${productName}*\n\nالكمية المتاحة: ${newStock} قطعة\n\nسارع بطلبه قبل نفاده: ${window.location.origin}/product/${productId}\n\n— ${settings.storeName}`
      );
      window.open(`https://wa.me/${phone}?text=${msg}`, '_blank', 'noopener,noreferrer');

      await supabase
        .from('stock_notifications')
        .update({ status: 'notified', notified_at: new Date().toISOString() })
        .eq('id', sub.id);

      count++;
      setSentCount(count);

      // Small delay between opens to avoid browser blocking
      if (subscribers.indexOf(sub) < subscribers.length - 1) {
        await new Promise(r => setTimeout(r, 800));
      }
    }

    setSending(false);
    setDone(true);
    onDone(count);
  };

  const pendingCount = subscribers.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
              <i className="ri-notification-4-fill text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
                إشعار جماعي عند إعادة التخزين
              </h2>
              <p className="text-emerald-100 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم تحديث مخزون المنتج إلى {newStock} قطعة
              </p>
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
              <i className="ri-box-3-line text-emerald-600 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#1a1a1a] truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {productName}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                المخزون الجديد: <span className="font-bold text-emerald-600">{newStock} قطعة</span>
              </p>
            </div>
            <div className="flex items-center gap-1.5 bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"></span>
              متوفر الآن
            </div>
          </div>
        </div>

        {/* Subscribers */}
        <div className="px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
            </div>
          ) : done ? (
            /* ── Success State ── */
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl"></i>
              </div>
              <p className="text-lg font-black text-[#1a1a1a] mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم الإرسال بنجاح!
              </p>
              <p className="text-sm text-gray-500" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم إشعار <span className="font-bold text-emerald-600">{sentCount}</span> مشترك عبر واتساب
              </p>
            </div>
          ) : pendingCount === 0 ? (
            /* ── No Subscribers ── */
            <div className="flex flex-col items-center py-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mb-3">
                <i className="ri-notification-off-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-sm font-bold text-gray-600 mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                لا يوجد مشتركون بانتظار الإشعار
              </p>
              <p className="text-xs text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
                لم يسجّل أحد للإشعار عند توفر هذا المنتج
              </p>
            </div>
          ) : (
            /* ── Subscribers List ── */
            <>
              {/* Count Banner */}
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                  <i className="ri-group-line text-amber-600 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-black text-amber-800" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {pendingCount} مشترك بانتظار الإشعار
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    سيتم فتح واتساب لكل مشترك تلقائياً
                  </p>
                </div>
              </div>

              {/* Subscribers Preview */}
              <div className="max-h-48 overflow-y-auto flex flex-col gap-2 mb-4">
                {subscribers.map((sub, idx) => (
                  <div
                    key={sub.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                      sending && sentCount > idx
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${
                      sending && sentCount > idx ? 'bg-emerald-100' : 'bg-white border border-gray-200'
                    }`}>
                      {sending && sentCount > idx ? (
                        <i className="ri-check-line text-emerald-600 text-xs"></i>
                      ) : sending && sentCount === idx ? (
                        <i className="ri-loader-4-line animate-spin text-amber-500 text-xs"></i>
                      ) : (
                        <i className="ri-user-line text-gray-400 text-xs"></i>
                      )}
                    </div>
                    <p className="text-sm font-medium text-[#1a1a1a] flex-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {sub.whatsapp || sub.phone}
                    </p>
                    {sending && sentCount > idx && (
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                        تم الإرسال
                      </span>
                    )}
                    {sending && sentCount === idx && (
                      <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                        جاري...
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Progress Bar (while sending) */}
              {sending && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <span>جاري الإرسال...</span>
                    <span className="font-bold text-emerald-600">{sentCount} / {pendingCount}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${(sentCount / pendingCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Info Note */}
              {!sending && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 mb-4">
                  <i className="ri-information-line text-blue-400 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-blue-600 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    سيتم فتح نافذة واتساب لكل مشترك بشكل تلقائي. تأكد من السماح بالنوافذ المنبثقة في المتصفح.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          {done || pendingCount === 0 ? (
            <button
              onClick={onClose}
              className="flex-1 bg-[#1a1a1a] text-white rounded-xl py-3 text-sm font-bold hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-check-line ml-1.5"></i>
              إغلاق
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={sending}
                className="px-5 py-3 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap rounded-xl hover:bg-gray-100 disabled:opacity-40"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                تخطي
              </button>
              <button
                onClick={handleSendAll}
                disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl py-3 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    جاري الإرسال ({sentCount}/{pendingCount})...
                  </>
                ) : (
                  <>
                    <i className="ri-whatsapp-line text-base"></i>
                    إشعار {pendingCount} مشترك عبر واتساب
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
